<script setup lang="ts">
import BrandMark from '@/components/BrandMark.vue'

defineProps<{ page: 'privacy' | 'terms' }>()

const COMPANY = 'Vitalcode, Inc.'
const CONTACT_EMAIL = 'ghafran@vitalcode.io'
const UPDATED = 'September 25, 2026'
</script>

<template>
  <div class="legal-page">
    <header class="legal-page__header"><RouterLink to="/"><BrandMark /></RouterLink>
      <nav><RouterLink to="/privacy">Privacy</RouterLink><RouterLink to="/terms">Terms</RouterLink></nav></header>

    <article v-if="page === 'privacy'" class="legal-page__body">
      <h1>Privacy Policy</h1>
      <p class="legal-page__meta">Last updated {{ UPDATED }}</p>
      <p>SkipHR is operated by {{ COMPANY }} ("we", "us"). This policy explains what we collect when you use SkipHR, how we use it, and your choices.</p>

      <h2>Information we collect</h2>
      <ul>
        <li><strong>Account information:</strong> your name, email address, time zone, and sign-in details.</li>
        <li><strong>Workspace content:</strong> messages to your Manager, goals, projects, documents you upload, files and media your agents create, and email or text messages received by your Manager's inbox and phone numbers.</li>
        <li><strong>Connections:</strong> credentials you choose to connect. They are stored encrypted in a secrets manager and are never shown back to you or to the AI agents that use them.</li>
        <li><strong>Mobile number:</strong> if you add one, your mobile phone number and the texts you exchange with your Manager.</li>
        <li><strong>Billing and usage:</strong> credit purchases (payments are processed by our payment provider; we do not store card numbers) and records of work performed.</li>
      </ul>

      <h2>How we use information</h2>
      <p>We use your information to provide and secure SkipHR, run the work you request, bill credits, send service messages, and provide support. Your workspace content is processed by the AI and cloud service providers that power SkipHR, only to perform your requests. We do not sell your personal information.</p>

      <h2>Text messaging</h2>
      <p>If you add and verify your mobile number, we use it to send verification codes and messages from your Manager, and to receive your texts. <strong>We do not share your mobile number or SMS opt-in consent with third parties or affiliates for their marketing or promotional purposes.</strong> Text messaging originator opt-in data and consent are not shared with any third parties, except service providers that deliver the messages. You can opt out at any time by replying STOP.</p>

      <h2>Sharing</h2>
      <p>We share information only with service providers that help us operate SkipHR (hosting, AI processing, email and SMS delivery, payments), when you direct us to (for example, when an agent posts to an account you connected), or when required by law.</p>

      <h2>Retention and deletion</h2>
      <p>We keep your information while your account is active. You can export your data or delete your account in Settings; deleted accounts are purged after a short recovery period. Received email files are kept for 30 days.</p>

      <h2>Security</h2>
      <p>We use encryption in transit and at rest, isolated agent workspaces, least-privilege access, and approval gates for sensitive actions. No system is perfectly secure, so please use a strong, unique password.</p>

      <h2>Your choices</h2>
      <p>You can access, correct, export, or delete your information in the app, or by emailing us. You can remove your mobile number or reply STOP to stop texts.</p>

      <h2>Contact</h2>
      <p>{{ COMPANY }} · <a :href="`mailto:${CONTACT_EMAIL}`">{{ CONTACT_EMAIL }}</a></p>
    </article>

    <article v-else class="legal-page__body">
      <h1>Terms of Service</h1>
      <p class="legal-page__meta">Last updated {{ UPDATED }}</p>
      <p>These terms govern your use of SkipHR, provided by {{ COMPANY }}. By using SkipHR you agree to them.</p>

      <h2>The service</h2>
      <p>SkipHR provides an AI Manager and agents that plan and carry out work you request. AI output can be wrong; review important results before relying on them. You are responsible for the instructions you give, the accounts you connect, and actions you approve.</p>

      <h2>Credits and billing</h2>
      <p>Work is paid for with prepaid credits. Plans show estimated credits before you confirm; actual usage is charged as work runs. Work pauses when your balance reaches zero. Credits are non-refundable except where required by law.</p>

      <h2>Acceptable use</h2>
      <p>Do not use SkipHR for unlawful, harmful, deceptive, or abusive activity, to send spam or unsolicited messages, or to violate the terms of third-party services you connect.</p>

      <h2 id="sms">SMS terms</h2>
      <ul>
        <li><strong>Program:</strong> SkipHR account notifications. After you add and verify your mobile number in SkipHR, you receive verification codes and messages from your AI Manager about your work, and you can text your Manager.</li>
        <li><strong>Frequency:</strong> message frequency varies based on your activity and settings.</li>
        <li><strong>Cost:</strong> message and data rates may apply. Texts with your Manager also use SkipHR credits, shown in the app.</li>
        <li><strong>Opt out:</strong> reply <strong>STOP</strong> to any message to stop receiving texts. Reply <strong>HELP</strong> for help, or email <a :href="`mailto:${CONTACT_EMAIL}`">{{ CONTACT_EMAIL }}</a>.</li>
        <li>Carriers are not liable for delayed or undelivered messages.</li>
        <li>See our <RouterLink to="/privacy">Privacy Policy</RouterLink> for how we handle your number.</li>
      </ul>

      <h2>Your content</h2>
      <p>You own your content and the work product agents create for you. You give us permission to process it only to provide the service.</p>

      <h2>Disclaimers and liability</h2>
      <p>SkipHR is provided "as is" without warranties of any kind. To the extent permitted by law, our total liability is limited to the amount you paid us in the 12 months before the claim.</p>

      <h2>Changes and termination</h2>
      <p>We may update these terms and will notify you of material changes. You may stop using SkipHR at any time; we may suspend accounts that violate these terms.</p>

      <h2>Contact</h2>
      <p>{{ COMPANY }} · <a :href="`mailto:${CONTACT_EMAIL}`">{{ CONTACT_EMAIL }}</a></p>
    </article>
  </div>
</template>
